import { Button, Input, Stack, Typography } from "@mui/joy";
import { ClipboardEvent, KeyboardEvent, useRef } from "react";
import { useTranslation } from "react-i18next";

interface EmailInputProps {
  email: string;
  onEmailChange: (email: string) => void;
  onSubmit: () => void;
  isLoading?: boolean;
}

export function EmailInput({
  email,
  onEmailChange,
  onSubmit,
  isLoading,
}: EmailInputProps) {
  const { t } = useTranslation();

  // Check if email is empty or contains gmail.com
  const isEmailEmpty = !email.trim();
  const isGmailUser = email.toLowerCase().includes("@gmail.com");

  // Disable button if email is empty or if it's a Gmail user
  const isButtonDisabled = isEmailEmpty || isGmailUser || isLoading;

  return (
    <Stack spacing={1} sx={{ width: "100%" }}>
      <Typography
        level="body-sm"
        color="warning"
        sx={{
          textAlign: "left",
          fontSize: "0.875rem",
          lineHeight: 1.4,
        }}
      >
        {t("auth.tempEmailWarning")}
      </Typography>

      <Typography
        level="body-sm"
        color="warning"
        sx={{
          textAlign: "left",
          fontSize: "0.875rem",
          lineHeight: 1.4,
        }}
      >
        {t("auth.gmailUserMessage")}
      </Typography>

      <Input
        type="email"
        placeholder={t("auth.enterYourEmail")}
        value={email}
        onChange={(e) => onEmailChange(e.target.value)}
        disabled={isLoading}
        sx={{ fontSize: "1rem" }}
      />

      <Button
        onClick={(e) => {
          if (isLoading) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          onSubmit();
        }}
        loading={isLoading}
        disabled={isButtonDisabled}
        sx={{ fontSize: "1rem" }}
      >
        {t("auth.sendVerificationCode")}
      </Button>
    </Stack>
  );
}

interface VerificationInputProps {
  verificationCode: string;
  onCodeChange: (code: string) => void;
  onSubmit: () => void;
  isLoading?: boolean;
}

export function VerificationInput({
  verificationCode,
  onCodeChange,
  onSubmit,
  isLoading,
}: VerificationInputProps) {
  const { t } = useTranslation();
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = (index: number, value: string) => {
    // Only allow single digit
    const digit = value.slice(-1);

    // Only allow numbers
    if (!/^[0-9]$/.test(digit)) return;

    // Update the code
    const newCode = verificationCode.split("");
    newCode[index] = digit;
    onCodeChange(newCode.join(""));

    // Move to next input if not the last box
    if (index < 5) {
      // Use setTimeout to ensure the focus happens after the value update
      setTimeout(() => {
        const nextInput = inputRefs.current[index + 1];
        if (nextInput) {
          nextInput.focus();
          nextInput.select();
        }
      }, 0);
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      if (verificationCode[index]) {
        // Clear current input
        const newCode = verificationCode.split("");
        newCode[index] = "";
        onCodeChange(newCode.join(""));
      } else if (index > 0) {
        // Move to previous input if current is empty
        const prevInput = inputRefs.current[index - 1];
        if (prevInput) {
          prevInput.focus();
          prevInput.select();
        }
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < 5) {
      e.preventDefault();
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 6);
    if (!/^[0-9]*$/.test(pastedData)) return;
    onCodeChange(pastedData.padEnd(6, ""));

    // Focus the next empty input or the last input
    const nextEmptyIndex = pastedData.length < 6 ? pastedData.length : 5;
    inputRefs.current[nextEmptyIndex]?.focus();
  };

  return (
    <Stack spacing={1} sx={{ width: "100%" }}>
      <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 2 }}>
        {Array(6)
          .fill(0)
          .map((_, index) => (
            <input
              key={index}
              type="text"
              value={verificationCode[index] || ""}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              maxLength={1}
              disabled={isLoading}
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
              style={{
                width: "40px",
                height: "40px",
                textAlign: "center",
                fontSize: "1.2rem",
                border: "1px solid var(--joy-palette-neutral-300)",
                borderRadius: "8px",
                backgroundColor: "var(--joy-palette-background-surface)",
                color: "var(--joy-palette-text-primary)",
                outline: "none",
                transition: "all 0.2s ease",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "var(--joy-palette-primary-500)";
                e.target.style.boxShadow =
                  "0 0 0 2px var(--joy-palette-primary-200)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "var(--joy-palette-neutral-300)";
                e.target.style.boxShadow = "none";
              }}
            />
          ))}
      </Stack>
      <Button
        onClick={onSubmit}
        loading={isLoading}
        disabled={verificationCode.length !== 6 || isLoading}
        sx={{ fontSize: "1rem" }}
      >
        {t("auth.verifyCode")}
      </Button>
    </Stack>
  );
}
